import type { z } from 'zod';
import { animalSchema } from './animal';
import { baseEventSchema, eventSchema } from './event';
import { dailyMilkUsageSchema, milkRecordSchema } from './milk';
import { healthProtocolSchema, reminderSchema } from './breeding';
import {
  buyerSchema,
  expenseCategorySchema,
  expenseSchema,
  fileAssetSchema,
  paymentSchema,
  recurringExpenseSchema,
  saleSchema,
} from './money';
import type { EntityName } from '../sync';
import { partnerSchema, partnerContributionSchema, shareGrantSchema, distributionSchema } from './partners';
import { projectionAssumptionSetSchema, projectionScenarioSchema } from '../projection';

export * from './common';
export * from './animal';
export * from './event';
export * from './event-data';
export * from './milk';
export * from './breeding';
export * from './money';
export * from './partners';

/** Validation schema per synced entity (G-7): used by web forms, the device repo and the API. */
export const entitySchemas: Record<EntityName, z.ZodType<Record<string, unknown>>> = {
  animal: animalSchema,
  event: eventSchema,
  milkRecord: milkRecordSchema,
  dailyMilkUsage: dailyMilkUsageSchema,
  reminder: reminderSchema,
  healthProtocol: healthProtocolSchema,
  buyer: buyerSchema,
  sale: saleSchema,
  payment: paymentSchema,
  expenseCategory: expenseCategorySchema,
  expense: expenseSchema,
  recurringExpense: recurringExpenseSchema,
  fileAsset: fileAssetSchema,
  partner: partnerSchema,
  partnerContribution: partnerContributionSchema,
  shareGrant: shareGrantSchema,
  distribution: distributionSchema,
  projectionAssumptionSet: projectionAssumptionSetSchema,
  projectionScenario: projectionScenarioSchema,
} as unknown as Record<EntityName, z.ZodType<Record<string, unknown>>>;

/** Client field names per entity (the keys a full upsert replaces). */
export const entityClientFields: Record<EntityName, string[]> = {
  animal: Object.keys(animalSchema.shape),
  event: Object.keys(baseEventSchema.shape),
  milkRecord: Object.keys(milkRecordSchema.shape),
  dailyMilkUsage: Object.keys(dailyMilkUsageSchema.shape),
  reminder: Object.keys(reminderSchema.shape),
  healthProtocol: Object.keys(healthProtocolSchema.shape),
  buyer: Object.keys(buyerSchema.shape),
  sale: Object.keys(saleSchema.shape),
  payment: Object.keys(paymentSchema.shape),
  expenseCategory: Object.keys(expenseCategorySchema.shape),
  expense: Object.keys(expenseSchema.shape),
  recurringExpense: Object.keys(recurringExpenseSchema.shape),
  fileAsset: Object.keys(fileAssetSchema.shape),
  partner: Object.keys(partnerSchema.shape),
  partnerContribution: ['partnerId', 'date', 'type', 'description', 'valueSantim', 'cashReceivedSantim', 'animalId', 'note', 'clientCreatedAt'],
  shareGrant: ['partnerId', 'percentBp', 'reason', 'description', 'startDate', 'endDate', 'clientCreatedAt'],
  distribution: Object.keys(distributionSchema.shape),
  projectionAssumptionSet: Object.keys(projectionAssumptionSetSchema.shape),
  projectionScenario: Object.keys(projectionScenarioSchema.shape),
};

/** Business-date columns (Postgres DATE ↔ "YYYY-MM-DD") per entity. */
export const entityDateFields: Record<EntityName, string[]> = {
  animal: ['birthDate', 'purchaseDate', 'exitDate'],
  event: ['date'],
  milkRecord: ['date'],
  dailyMilkUsage: ['date'],
  reminder: ['dueDate', 'windowEnd'],
  healthProtocol: [],
  buyer: [],
  sale: ['date'],
  payment: ['date'],
  expenseCategory: [],
  expense: ['date'],
  recurringExpense: ['startMonth', 'endMonth'],
  fileAsset: [],
  partner: ['joinedOn'],
  partnerContribution: ['date'],
  shareGrant: ['startDate', 'endDate'],
  distribution: ['periodFrom', 'periodTo', 'decidedOn', 'sharesAsOf'],
  projectionAssumptionSet: [],
  projectionScenario: [],
};

/** Timestamp columns (ISO-8601 UTC on the wire) per entity, beyond the sync block. */
export const entityTimestampFields: Record<EntityName, string[]> = {
  animal: [],
  event: [],
  milkRecord: [],
  dailyMilkUsage: [],
  reminder: ['completedAt'],
  healthProtocol: [],
  buyer: [],
  sale: [],
  payment: [],
  expenseCategory: [],
  expense: [],
  recurringExpense: [],
  fileAsset: ['uploadedAt'],
  partner: [],
  partnerContribution: [],
  shareGrant: [],
  distribution: [],
  projectionAssumptionSet: [],
  projectionScenario: [],
};

export const SYNC_TIMESTAMP_FIELDS = ['clientCreatedAt', 'createdAt', 'updatedAt', 'deletedAt'] as const;
